import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Layout, Plus, Timer, CheckCircle2, Crown } from "lucide-react";
import { motion, useAnimation } from "framer-motion";
import { supabase } from "@/lib/supabase";

export default function Tutorial() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const navigate = useNavigate();
  const controls = useAnimation();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    };
    checkAuth();
  }, []);

  const steps = [
    {
      title: "CONGRATULATIONS! 🎉",
      description: "You Just Leveled Up Your Life with DoDue! 🚽\n\n\"Procrastination is the grave in which opportunity is buried\"... Our Management tool will Help you flush away procrastination!",
      icon: <Layout className="w-12 h-12 text-indigo-400" />,
    },
    {
      title: "Become the CEO of Your Life! 👔",
      description: "Start by creating DoDue projects to organize your tasks and build your success HeadQuarters! Each project comes pre-loaded with To Do, In Progress, and Done columns. I like to call it your Battle Plan, Victory March and Trophy Case!",
      icon: <Plus className="w-12 h-12 text-purple-400" />,
    },
    {
      title: "Track Your DoDue Flow! 🌊",
      description: "Flow Like a pro, move tasks between columns with simple radio buttons or drag and drop integrated features. Track your progress and watch it flow from Start to Finish!",
      icon: <Timer className="w-12 h-12 text-blue-400" />,
    },
    {
      title: "Keep It Fresh and Clean! ✨",
      description: "Add notes, set priorities, track progress, and keep your DoDue game immaculate. Every completed task comes with a personal Success Parade!",
      icon: <CheckCircle2 className="w-12 h-12 text-green-400" />,
    },
    {
      title: "Level Up to Premium Ply! 👑",
      description: "All features are now FREE! Enjoy unlimited cards, advanced features, and analytics - no subscription required. Create as many projects as you want and make the most of all our premium features!",
      icon: <Crown className="w-12 h-12 text-yellow-400" />,
      stamp: (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-[-20deg] border-8 border-green-500 rounded-lg px-8 py-4">
          <span className="text-4xl font-bold text-green-500">FREE!</span>
        </div>
      )
    },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      controls.start({
        opacity: [0, 1],
        x: [50, 0],
        transition: { duration: 0.5 }
      });
      setCurrentStep(prev => prev + 1);
    } else {
      // If it's the last step, check authentication
      if (isAuthenticated) {
        navigate('/boards');
      } else {
        navigate('/login');
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      controls.start({
        opacity: [0, 1],
        x: [-50, 0],
        transition: { duration: 0.5 }
      });
      setCurrentStep(prev => prev - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <div className="p-4 border-b border-gray-700">
        <div className="flex items-center space-x-4">
          <Link to={isAuthenticated ? "/boards" : "/"}>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-gray-300 hover:text-white"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              {isAuthenticated ? "Back to My DoDue Space" : "Back to Home"}
            </Button>
          </Link>
          <h1 className="text-xl font-semibold">Tutorial</h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto mt-12 p-6">
        <motion.div 
          animate={controls}
          className="bg-slate-800 rounded-lg p-8 shadow-lg"
        >
          <div className="flex justify-center mb-8 relative">
            {steps[currentStep].icon}
            {steps[currentStep].stamp}
          </div>

          <h2 className="text-2xl font-bold mb-4 text-center">
            {steps[currentStep].title}
          </h2>

          <p className="text-gray-300 text-center mb-8 whitespace-pre-line">
            {steps[currentStep].description}
          </p>

          <div className="flex justify-between items-center mt-8">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="text-gray-300 hover:text-white"
            >
              Previous
            </Button>

            <div className="flex space-x-2">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full ${
                    index === currentStep ? 'bg-indigo-500' : 'bg-gray-600'
                  }`}
                />
              ))}
            </div>

            <Button
              variant="default"
              onClick={handleNext}
              className="bg-indigo-500 hover:bg-indigo-600"
            >
              {currentStep === steps.length - 1 ? "Get Started!" : "Next"}
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
